import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import {TableColumn, FieldType, TableColumnAction} from './table-column';
import {formatDate, formatNumber, formatDollar} from '../../../wc-converters/index';
import {TableCellEditText} from './editors/table-cell-edit-text';
import {TableCellEditNumber} from './editors/table-cell-edit-number';
import {Icon} from '../icon/icon';
import {TableAlert} from './table-alert';


Vue.config.keyCodes.f2 = 113;

@Component({
  template: require('./table-body-cell.html'),
  name: 'wc-table-body-cell',
  components: {
    'wc-table-cell-edit-text': TableCellEditText,
    'wc-table-cell-edit-number': TableCellEditNumber,
    'wc-icon': Icon
  }
})
export class TableBodyCell extends Vue {
  @Prop()
  group: any;
  @Prop()
  row: any;
  @Prop()
  column: TableColumn;
  @Prop()
  visibleColumns: Array<TableColumn>;
  @Prop()
  data: Array<any>;
  @Prop()
  navigatable: boolean;
  @Prop()
  navigation: any;
  @Prop()
  navigationRowLength: number;
  @Prop()
  navigationColumnLength: number;
  @Prop({
    default: 2
  })
  clicksToEdit: number;
  @Prop()
  tableAlerts: Array<TableAlert>;
  @Prop()
  rowIndex: number;

  delay = 500;
  clicks = 0;
  timer: number;
  editing: boolean = false;
  fieldType = FieldType;
  isCellEditEnd;

  get focused() {
    return this.navigation && this.navigation.column === this.column && this.navigation.row === this.row;
  }

  get getCellWidth() {
    if (this.column.width) {
      return this.column.width;
    } else if (this.editing === true) {
      return (this.$refs.tableCell as HTMLTableCellElement).offsetWidth + 'px';
    }

    return undefined;
  }
  
  mounted() {
    this.focusNavigation();
  }

  @Watch('navigation')
  focusNavigation() {
    if (this.focused && this.$refs.tableCell) 
      (this.$refs.tableCell as HTMLTableCellElement).focus();
  }

  onCellClick(e:any, row: any, column: TableColumn) {
    this.setNavigationCell(row, column);

    if (!column.editable || row.__editing) return;

    this.clicks++;    
    if (this.clicksToEdit === this.clicks) {
      clearTimeout(this.timer);  
      this.clicks = 0;
      this.editCell();
    } else {
      this.timer = window.setTimeout(() => {
        this.clicks = 0;
      }, this.delay);
    }
    // prevent event bubble for editable cells
    e.stopPropagation();
  }

  
  onCellActionClick(tableColumnAction: TableColumnAction) {
    if (tableColumnAction.click)
      tableColumnAction.click.call(this, this.row[this.column.field], this.row, this.column);
  }

  editCell() {
    if (!this.column.editable) return;
    clearTimeout(this.isCellEditEnd);
    this.editing = true;
  }

  dataEdit(row, column) {
    row.__dirty = true;
    this.$emit('dataEdit', row, column);
  }

  cellEditEnd() {
    // blur timeout to prevent refocus
    this.isCellEditEnd = setTimeout(() => {
      this.editing = false;
      this.focusNavigation();
    }, 250);
  }

  cellAlert(row: any, column: TableColumn) {
    return this.tableAlerts.find(alert => {
      return alert.row === row && alert.field === column.field;
    })
  }

  // Cell navigation
  setNavigationCell(row: any, column: TableColumn) {
    if (this.navigatable) {
      this.$emit('navigation', {
        row: row,
        column: column
      });
    }
  }

  navigate(e: KeyboardEvent) {
    if (e.keyCode === KeyboardCode.Tab || e.keyCode === KeyboardCode.F2) {
      if (e.keyCode === KeyboardCode.Tab && e.shiftKey) this.navigateLeft();
      else if (e.keyCode === KeyboardCode.Tab) this.navigateRight();
      else if (e.keyCode === KeyboardCode.F2) {
        this.editCell();
      }
    } else {
      if (e.keyCode === KeyboardCode.UpArrow) this.navigateUp();
      else if (e.keyCode === KeyboardCode.RightArrow) this.navigateRight();
      else if (e.keyCode === KeyboardCode.DownArrow) this.navigateDown();
      else if (e.keyCode === KeyboardCode.LeftArrow) {
        this.navigateLeft();
      }
    }
    // only prevent bubble if we use custom naviagation
    if(this.navigatable){
      e.preventDefault();
    }
  }
  
  navigateRight() {
    if (!this.navigatable) return;
    let columnIndex = this.visibleColumns.indexOf(this.navigation.column);
    // Last column
    if (columnIndex === this.visibleColumns.length - 1) return;
    columnIndex += 1;
    let column = this.visibleColumns[columnIndex];
    this.setNavigationCell(this.navigation.row, column);
  }

  navigateLeft() {
    if (!this.navigatable) return;
    let columnIndex = this.visibleColumns.indexOf(this.navigation.column);
    // Last column
    if (columnIndex === 0) return;
    columnIndex -= 1;
    let column = this.visibleColumns[columnIndex];
    this.setNavigationCell(this.navigation.row, column);
  }

  navigateDown() {
    if (!this.navigatable) return;
    let rowIndex = this.group.data.indexOf(this.navigation.row);
    // Last column
    if (rowIndex === this.group.data.length - 1) return;
    rowIndex += 1;
    let row = this.group.data[rowIndex];
    this.setNavigationCell(row, this.navigation.column);
  }

  navigateUp() {
    if (!this.navigatable) return;
    let rowIndex = this.group.data.indexOf(this.navigation.row);
    // Last column
    if (rowIndex === 0) return;
    rowIndex -= 1;
    let row = this.group.data[rowIndex];
    this.setNavigationCell(row, this.navigation.column);
  }
}

Vue.filter('cellFormatter', (value: any, row: any, column: TableColumn) => {
  if (column.fieldType) {
    switch(column.fieldType) {
      case FieldType.Date:
      return formatDate(value,'dd/MM/yyyy');
      case FieldType.DateTime: 
        return formatDate(value);
      case FieldType.Dollar: 
        return formatDollar(value);
      case FieldType.Number: 
        return formatNumber(value, '0,00');
      case FieldType.Decimal: 
        return formatNumber(value, '0,00.00');
      case FieldType.String:
        return value;
    }
  }

  if (column.formatter) {
    return column.formatter.call(this, value, row, column);
  }

  return value;
});

export enum KeyboardCode {
  Tab = 9,
  Enter = 13,
  Shift = 16,
  Escape = 27,
  LeftArrow = 37,
  UpArrow = 38,
  RightArrow = 39,
  DownArrow = 40,
  Insert = 45,
  Delete = 46,
  F2 = 113
}


// WEBPACK FOOTER //
// src/../~/wc-lib/src/wc-ui/components/table/table-body-cell.ts